import { parseISO, startOfMonth, startOfQuarter, startOfYear } from 'date-fns'
import JsonLogic, { RulesLogic } from 'json-logic-js'
import { Enums, Tables } from '../db_types'
import DolfinDSL, {
  ACTIVITIES_COLUMNS,
  DEALS_COLUMNS,
  DEAL_LINE_ITEMS_COLUMNS,
  INVOICES_COLUMNS,
  LINE_ITEMS_COLUMNS
} from '../formula-parser'
import { CustomFieldParser } from '../formula-parser/parsers/custom-field-parser'
import { OperatorsParser } from '../formula-parser/parsers/operators'
import { RecordParser } from '../formula-parser/parsers/record-parser'
import DolfinRecord, { isProviderFilterVariable } from '../record'
import { RecordData } from '../types'

const normalizeEvaluatedValue = (value: unknown) =>
  /^(true|false)$/.test(String(value))
    ? String(value) === 'true'
    : value !== '' && value !== null && value !== undefined && !isNaN(Number(value))
      ? Number(value)
      : value

class LogicEvaluator {
  constructor(
    private source: Enums<'plan_source'>,
    private customFields: { [name: string]: Tables<'custom_fields'> }
  ) {
    JsonLogic.add_operation('startswith', (haystack: string, needle: string) =>
      haystack.startsWith(needle)
    )
    JsonLogic.add_operation('endswith', (haystack: string, needle: string) =>
      haystack.endsWith(needle)
    )
    JsonLogic.add_operation(
      'samemonth',
      (date1: string, date2: string) =>
        startOfMonth(parseISO(date1)).getTime() === startOfMonth(parseISO(date2)).getTime()
    )
    JsonLogic.add_operation(
      'samequarter',
      (date1: string, date2: string) =>
        startOfQuarter(parseISO(date1)).getTime() === startOfQuarter(parseISO(date2)).getTime()
    )

    JsonLogic.add_operation(
      'futurequarter',
      (date1: string, date2: string) =>
        startOfQuarter(parseISO(date1)).getTime() > startOfQuarter(parseISO(date2)).getTime()
    )

    JsonLogic.add_operation(
      'pastquarter',
      (date1: string, date2: string) =>
        startOfQuarter(parseISO(date1)).getTime() < startOfQuarter(parseISO(date2)).getTime()
    )

    JsonLogic.add_operation(
      'sameyear',
      (date1: string, date2: string) =>
        startOfYear(parseISO(date1)).getTime() === startOfYear(parseISO(date2)).getTime()
    )

    JsonLogic.add_operation('contains', (haystack: string, needle: string) => {
      return haystack.toLowerCase().includes(needle.toLowerCase())
    })

    JsonLogic.add_operation(
      'doesNotContain',
      (haystack: string, needle: string) => !haystack.toLowerCase().includes(needle.toLowerCase())
    )
  }

  private parseVariable(variable: string, recordData: DolfinRecord) {
    if (isProviderFilterVariable(variable, this.source)) {
      return normalizeEvaluatedValue(recordData.getFieldValue(variable, this.customFields))
    }

    const parsers = [
      new CustomFieldParser(recordData, [], this.customFields, DolfinDSL.META_REGEXP),
      new RecordParser(
        recordData,
        new RegExp(
          `^(${(this.source === 'deal'
            ? [...DEALS_COLUMNS, 'owner_id']
            : this.source === 'activity'
              ? [...ACTIVITIES_COLUMNS, 'owner_id', 'deal.owner_id']
              : this.source === 'invoice'
                ? [...INVOICES_COLUMNS, 'deal.owner_id']
                : this.source === 'line_item'
                  ? [...LINE_ITEMS_COLUMNS, 'deal.owner_id']
                  : this.source === 'deal_line_item'
                    ? [...DEAL_LINE_ITEMS_COLUMNS, 'deal.owner_id']
                    : ['period_start', 'profile_id']
          ).join('|')})$`
        )
      ),
      new OperatorsParser(recordData, DolfinDSL.OPERATOR_REGEXP)
    ]

    let formula = variable
    for (const parser of parsers) {
      formula = parser.execute(formula)
    }

    if (formula.startsWith('(') && formula.endsWith(')')) {
      formula = new Function(`return ${formula}`)()
    }

    return normalizeEvaluatedValue(formula)
  }

  getParsedItemData(
    filters: Record<string, unknown>,
    item: RecordData | DolfinRecord,
    additionalFields?: string[],
    userFields?: Record<string, unknown>
  ) {
    const filtersVars = JsonLogic.uses_data(filters)
    additionalFields?.forEach(f => {
      if (!filtersVars.includes(f)) {
        filtersVars.push(f)
      }
    })

    const recordData = item instanceof DolfinRecord ? item : new DolfinRecord(this.source, item)

    const parsedItemData: Record<string, unknown> = {}
    for (const filtersVar of filtersVars) {
      if (filtersVar.startsWith('user.')) {
        parsedItemData[filtersVar] = userFields?.[filtersVar.replace(/^user\./, '')]
      } else {
        parsedItemData[filtersVar] = this.parseVariable(filtersVar, recordData)
      }
    }

    if (Object.keys(parsedItemData).some(f => f.startsWith('deal.'))) {
      parsedItemData.deal = {}
    }
    if (Object.keys(parsedItemData).some(f => f.startsWith('invoice.'))) {
      parsedItemData.invoice = {}
    }
    if (Object.keys(parsedItemData).some(f => f.startsWith('user.'))) {
      parsedItemData.user = {}
    }

    for (const filtersVar of filtersVars) {
      if (filtersVar.startsWith('deal.')) {
        ;(parsedItemData.deal as Record<string, unknown>)[filtersVar.replace(/^deal\./, '')] =
          parsedItemData[filtersVar]
        delete parsedItemData[filtersVar]
      } else if (filtersVar.startsWith('invoice.')) {
        ;(parsedItemData.invoice as Record<string, unknown>)[filtersVar.replace(/^invoice\./, '')] =
          parsedItemData[filtersVar]
        delete parsedItemData[filtersVar]
      } else if (filtersVar.startsWith('user.')) {
        ;(parsedItemData.user as Record<string, unknown>)[filtersVar.replace(/^user\./, '')] =
          parsedItemData[filtersVar]
        delete parsedItemData[filtersVar]
      }
    }

    return parsedItemData
  }

  getParsedPeriodData(
    filters: Record<string, unknown>,
    data: Record<string, unknown>,
    userFields?: Record<string, unknown>,
    periodFields?: Record<string, unknown>
  ) {
    const filtersVars = JsonLogic.uses_data(filters)

    const parsedPeriodData: Record<string, unknown> = {}
    for (const filtersVar of filtersVars) {
      if (filtersVar.startsWith('user.')) {
        if (!parsedPeriodData.user) {
          parsedPeriodData.user = {}
        }
        ;(parsedPeriodData.user as Record<string, unknown>)[filtersVar.replace(/^user\./, '')] =
          userFields?.[filtersVar.replace(/^user\./, '')]
      } else if (filtersVar.startsWith('period.')) {
        if (!parsedPeriodData.period) {
          parsedPeriodData.period = {}
        }
        ;(parsedPeriodData.period as Record<string, unknown>)[filtersVar.replace(/^period\./, '')] =
          periodFields?.[filtersVar.replace(/^period\./, '')]
      } else {
        parsedPeriodData[filtersVar] = data[filtersVar]
      }
    }

    return parsedPeriodData
  }

  getMetricIds(filters: Record<string, unknown>) {
    return JsonLogic.uses_data(filters)
      .filter(f => f.startsWith('metric_'))
      .map(f => Number(f.replace(/^metric_/, '')))
  }

  getTargetIds(filters: Record<string, unknown>) {
    return JsonLogic.uses_data(filters)
      .filter(f => f.startsWith('target_'))
      .map(f => {
        const match = f.match(/^target_([0-9]+)/)
        return match ? Number(match[1]) : NaN
      })
      .filter(id => !isNaN(id))
  }

  getTargetData(filters: Record<string, unknown>) {
    return JsonLogic.uses_data(filters)
      .filter(f => f.startsWith('target_'))
      .map((f: string) => {
        const parts = f.match(/^target_([0-9]+)(_(month|quarter|half_year|year))?(_full_period)?$/)
        return {
          id: Number(parts?.[1]),
          period: parts?.[3] as Enums<'period'> | undefined,
          fullPeriod: !!parts?.[4]
        }
      })
  }

  evaluate(filters: Record<string, unknown>, recordData: Record<string, unknown>) {
    return JsonLogic.apply(filters as RulesLogic, recordData)
  }

  hasUserFields(filters: Record<string, unknown>) {
    return JsonLogic.uses_data(filters).some(f => f.startsWith('user.'))
  }
}

export default LogicEvaluator
